import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  HostBinding,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { AbbDateComponent } from '@components/abb-date/abb-date.component';
import {
  AbbTableComponent,
  DataTableFilter,
  FilterOption,
  IColumn,
} from '@components/abb-table/abb-table.component';
import {
  contractListQuery,
  contractTypesQuery,
  deleteContract,
} from '@graphql/contracts-and-tariffs';
import { DayOfWeekPipe } from '@pipes/day-of-week.pipe';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { OverlayService } from '@services/ui/overlay.service';
import { ToastService } from '@services/ui/toast.service';
import { dataProviderFromObservable } from '@utils/data-provider';
import { LOADING } from '@utils/loader';
import { Apollo } from 'apollo-angular';
import {
  ContractListQuery,
  ContractListQueryVariables,
  ContractTag,
  ContractType,
  ContractTypesQuery,
  ContractTypesQueryVariables,
  DeleteContractMutation,
  DeleteContractMutationVariables,
  Tariff,
} from 'gql/graphql';
import { BehaviorSubject, first, map, Subscription } from 'rxjs';
import { ContractSidePanelComponent } from './contract-side-panel/contract-side-panel.component';
import { getErrorMessage } from '@utils/errors';
import { TranslateService } from '@ngx-translate/core';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { PAGES } from '@constants/constants';
import { PermissionsService } from '@services/user/permissions.service';
import { MenuItem } from '@components/abb-actions-menu/abb-actions-menu.component';
import { t } from '@utils/strings';
type ContractsDataTableFilter = {
  name: string;
  typeId: string | null;
};

type ContractByTariff = {
  id: string;
  name: string;
  time: Tariff;
  tariff: string;
  tag: ContractTag;
  type: ContractType;
  devices: {
    id: string;
    name: string;
  }[];
  contractId: string;
};
@Component({
  selector: 'abb-costs-tariffs',
  standalone: true,
  imports: [
    AbbTableComponent,
    DayOfWeekPipe,
    CommonModule,
    AbbDateComponent,
    TPipe,
    OverflowTooltipDirective,
  ],
  template: `
    <abb-table
      [loading]="loading()"
      [columns]="columns()"
      [actions]="actions"
      [dataProvider]="dataProvider"
      [defaultFilters]="defaultFilters"
      [filters]="filters()"
      [defaultSortBy]="[{ column: 'name', order: 'asc' }]"
      [expandable]="false"
    ></abb-table>
    <ng-template #type let-item>
      <span overflowTooltip class="block truncate">{{ item.type.name | t }}</span>
    </ng-template>
    <ng-template #devices let-item>
      <span overflowTooltip class="block truncate">
        @for (device of item.devices; track device.id) {
        {{ device.name + ($last ? '' : ', ') }}
        }</span
      >
    </ng-template>
    <ng-template #tag let-item>
      <span overflowTooltip class="block truncate">{{ item.tag.name | t }}</span>
    </ng-template>
    <ng-template #tariff let-item>
      <span overflowTooltip class="block truncate">{{ item.tariff }}</span>
    </ng-template>
    <ng-template #time let-item>
      <span overflowTooltip class="block truncate">
        <small class="mr-1">
          {{ item.time.validFromTime }} / {{ item.time.validToTime }}
        </small>
        <small>
          @for (day of item.time.validDays; track day) {
          {{ (day | dayOfWeek) + ($last ? '' : ', ') }}
          }
        </small>
        <br />
        <small>
          <abb-date [date]="item.time.validFromDate"></abb-date> {{ 'to' | t }}
          <abb-date [date]="item.time.validToDate"></abb-date>
        </small>
      </span>
    </ng-template>
  `,
  styles: ``,
  host: {
    class: 'flex-1',
  },
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'property_services.tabs.costs_tariffs',
    },
  ],
})
export class CostsTariffsComponent {
  @HostBinding('attr.id') componentid = 'costs-tariffs-component';
  prefix = inject(TRANSLATION_PREFIX);
  loading = signal(true);
  canEdit = computed(() =>
    this.permissionsService.canEditPage(PAGES.PROPERTYSERVICES)
  );
  awaitingRes = signal<boolean>(false);
  typeTemplate = viewChild<TemplateRef<unknown>>('type');
  tagTemplate = viewChild<TemplateRef<unknown>>('tag');
  devicesTemplate = viewChild<TemplateRef<unknown>>('devices');
  tariffTemplate = viewChild<TemplateRef<unknown>>('tariff');
  timeTemplate = viewChild<TemplateRef<unknown>>('time');
  actionsTemplate = viewChild<TemplateRef<unknown>>('actions');
  contracts$ = new BehaviorSubject<ContractListQuery['contractList'] | null>(
    null
  );
  contractsByTariff$ = this.contracts$.pipe<ContractByTariff[]>(
    map((contracts) => {
      return (
        contracts?.flatMap((c) =>
          c.tariffs.map((t) => ({
            id: c.id + '-' + t.id,
            type: c.type,
            tag: c.tag,
            tariff: t.name,
            time: t,
            name: c.name,
            devices: c.devices,
            contractId: c.id,
          }))
        ) ?? []
      );
    })
  );
  columns = computed<IColumn<ContractByTariff>[]>(() => {
    return [
      {
        id: 'name',
        name: 'Name',
        translate: true,
        key: 'name',
      },
      {
        id: 'type',
        name: 'Type',
        templateRef: this.typeTemplate(),
        translate: true,
      },
      {
        id: 'devices',
        name: 'Devices',
        templateRef: this.devicesTemplate(),
        translate: true,
      },
      {
        id: 'tag',
        name: 'Tag',
        templateRef: this.tagTemplate(),
        translate: true,
      },
      {
        id: 'tariff',
        name: 'Tariff',
        templateRef: this.tariffTemplate(),
        translate: true,
      },
      {
        id: 'time',
        name: 'Time',
        templateRef: this.timeTemplate(),
        translate: true,
      },
      {
        name: 'actions',
        tableCellClass: this.canEdit() ? 'w-0' : 'hidden',
        tableHeaderCellClass: this.canEdit() ? 'w-0' : 'hidden',
      },
    ];
  });

  actions: Array<MenuItem<ContractByTariff>> = [
    {
      label: 'Edit Contract',
      action: (contracts) => this.edit(contracts[0].contractId),
    },
    {
      label: 'Delete Contract',
      action: (contracts) => (this.openDeleteModal(contracts[0].contractId)),
    },
  ];
  dataProvider = dataProviderFromObservable<
    ContractByTariff,
    ContractsDataTableFilter
  >(this.contractsByTariff$, (el, filter) => {
    return (
      (!filter.typeId || el.type.id === filter.typeId) &&
      el.name.toLowerCase().includes(filter.name.toLowerCase())
    );
  });
  defaultFilters: ContractsDataTableFilter = {
    typeId: null,
    name: '',
  };
  contractTypesSelectOptions = signal<FilterOption[]>([]);
  filters = computed<DataTableFilter<ContractsDataTableFilter>[]>(() => {
    return [
      {
        id: 'name',
        filterType: 'text',
        className: 'w-full',
        label: 'Contract Name',
      },
      {
        id: 'typeId',
        filterOptions: this.contractTypesSelectOptions(),
        filterType: 'select',
        className: 'w-full',
        label: 'Type',
      },
    ];
  });

  private subscriptions: Subscription[] = [];
  constructor(
    private readonly apollo: Apollo,
    private readonly toast: ToastService,
    private readonly translate: TranslateService,
    private readonly overlay: OverlayService,
    private readonly permissionsService: PermissionsService
  ) {
    this.subscriptions.push(
      apollo
        .watchQuery<ContractTypesQuery, ContractTypesQueryVariables>({
          query: contractTypesQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.contractTypesSelectOptions.set(
              data.contractTypesList.map((c) => ({
                value: c.id,
                label: c.name,
                skipTranslation: false,
              }))
            );
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),
      apollo
        .watchQuery<ContractListQuery, ContractListQueryVariables>({
          query: contractListQuery,
        })
        .valueChanges.subscribe({
          next: ({ data, loading }) => {
            this.contracts$.next(data.contractList);
            this.loading.set(loading);
          },
          error: (err) => {
            this.loading.set(false);
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }

  edit(itemId: string) {
    const item = this.contracts$.value?.find((c) => c.id === itemId);
    if (!item) {
      return;
    }
    this.overlay.openSidePanel(ContractSidePanelComponent, {
      mode: 'edit',
      initialValue: item,
    });
  }

  openDeleteModal(itemId: string) {
    const item = this.contracts$.value?.find((c) => c.id === itemId);
    if (item) {
      this.overlay
        .openAlert({
          message: `${t(
            this.prefix,
            'Are you sure you want to remove contract',
            this.translate
          )} <b>${item.name}</b>?`,
          htmlMessage: true,
          confirmText: t(this.prefix, 'Remove', this.translate),
        })
        .then(() => {
          this.awaitingRes.set(true);
          this.deleteContract(item.id).finally(() =>
            this.awaitingRes.set(false)
          );
        })
        .catch(() => console.log('cancel'));
    }
  }

  deleteContract(id: string) {
    return new Promise<void>((resolve, reject) =>
      this.apollo
        .mutate<DeleteContractMutation, DeleteContractMutationVariables>({
          mutation: deleteContract,
          variables: { id },
        })
        .pipe(first())
        .subscribe({
          next: () => {
            resolve();
            this.toast.notifySuccess(
              t(this.prefix, 'Contract removed successfully', this.translate)
            );
            this.contracts$.next(
              this.contracts$.value?.filter((g) => g.id !== id) ?? []
            );
          },
          error: (err) => {
            reject(new Error(err));
            let errMsg = getErrorMessage(err, this.translate);
            this.toast.notifyError(errMsg);
          },
        })
    );
  }
}
